import { useEffect, useState } from "react";
import { getUserToken } from "@/utils/userAuthenticate";
import instance from "@/axios";
import { useTranslation } from "react-i18next";
import "@/localisation/index";
import { BackButtonIcon, SaveButtonIcon, ExportIcon } from "@/components/Icons";
import SendTestEmail from "@/components/EmailTemplates/SendTestEmail";
import { sanitizeInput } from "@/hooks/useInputSanitization";
import { FaInfoCircle } from "react-icons/fa";
import MarkdownEditor from "./RichTextEditor";

export default function RenderEmailTemplate({ setViewType, template }) {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);

  const [emailConfig, setEmailConfig] = useState({
    subject: template.emailConfig?.emailSubject || "",
    senderName: template.emailConfig?.senderName || "",
    senderEmail: template.emailConfig?.senderEmail || "",
  });

  const [bodyData, setBodyData] = useState({
    bodyMessage: "",
    errorMsg: "",
    emailError: "",
    isEmailSending: false,
    isSavingTemplate: false,
    templateBannerImage:
      template.updatedValues?.template_image ||
      template.defaultValues?.template_image,
    merchantMessage:
      template.updatedValues?.message || template.defaultValues?.message || "",
  });

  const [termsAndConditions, setTermsAndConditions] = useState(
    template.updatedValues?.terms || template.defaultValues?.terms || ""
  );

  const saveTemplateChanges = async () => {
    try {
      setBodyData((prev) => ({ ...prev, errorMsg: "", isSavingTemplate: true }));

      const url = "/mail-template/update";
      const headers = { Authorization: getUserToken() };

      const body = {
        templateId: template.id,
        updateObj: {
          message: bodyData.merchantMessage,
          template_image: bodyData.templateBannerImage,
          terms: termsAndConditions,
        },
        emailConfig: {
          emailSubject: emailConfig.subject,
          senderName: emailConfig.senderName,
          senderEmail: emailConfig.senderEmail,
        },
      };

      const res = await instance.put(url, body, { headers });
      const { success, message } = res.data;

      if (success) {
        alert(t("emailTemplates.templateUpdated"));
        setViewType("TEMPLATE_LIST");
      } else {
        alert(message);
      }
    } catch (error) {
      console.log("error", error.message);
      alert(error.message);
    } finally {
      setBodyData((prev) => ({ ...prev, isSavingTemplate: false }));
    }
  };

  const BackButton = () => (
    <div className="back-button" onClick={() => setViewType("TEMPLATE_LIST")}
    >
      <div className="icon-container">
        <BackButtonIcon />
      </div>
      <span className="button-text">{t("emailTemplates.back")}</span>
    </div>
  );

  const SaveButton = () => (
    <div
      className="back-button"
      onClick={saveTemplateChanges}
      disabled={bodyData.isSavingTemplate}
    >
      <div className="icon-container">
        <SaveButtonIcon />
      </div>
      <span className="button-text">{t("emailTemplates.save")}</span>
    </div>
  );

  const handleAddImage = () => {
    const fileInput = document.getElementById("banner-file-picker");
    const file = fileInput.files[0];

    if (!file) {
      alert(t("createGiftcard.selectImage"));
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      const img = new Image();
      img.onload = () => {
        if (img.width === 600 && img.height === 250) {
          const uploadedImageUrl = reader.result;
          const brandImageEle = document.getElementById("template-banner-image");
          if (brandImageEle) brandImageEle.src = uploadedImageUrl;
          setBodyData((prev) => ({
            ...prev,
            templateBannerImage: uploadedImageUrl.split(",")[1],
          }));
        } else {
          alert(
            t("createGiftcard.modelImageInvalid") + `${img.width}x${img.height}`
          );
        }
      };
      img.src = reader.result;
    };

    reader.readAsDataURL(file);
  };

  return (
    <div className="template-container">
      <div className="template-container__header">
        <BackButton />
        <div className="template-container__header__right">
          <SendTestEmail templateId={template.id} type="send-as-gift" />
          <SaveButton />
        </div>
      </div>

      <div className="template-container__body">
        <div className="product">
          <div className="product__outline">
            <div className="product__header">
              {t("emailTemplates.emailPreview")}
            </div>
            <div dangerouslySetInnerHTML={{ __html: template.html_content }} />
          </div>
        </div>

        <div>
          <div className="email-configuration">
            <h2 className="email-configuration__heading">
              {t("emailTemplates.emailConfiguration")}
            </h2>

            <div className="email-configuration__field">
              <label
                htmlFor="email-subject"
                className="email-configuration__label"
              >
                {t("emailTemplates.emailSubject")}
              </label>
              <textarea
                rows={4}
                id="email-subject"
                placeholder={t("emailTemplates.enterEmailSubject")}
                value={emailConfig.subject}
                onChange={(e) =>
                  setEmailConfig((prev) => ({
                    ...prev,
                    subject: sanitizeInput(e.target.value, "textarea"),
                  }))
                }
              />
            </div>

            <div className="email-configuration__field" id="sender-name">
              <div className="tooltip-icon-container">
                <label className="email-configuration__label">
                  {t("emailTemplates.senderName")}
                </label>
                <div
                  className="tooltip-icon"
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  <FaInfoCircle size={18} color="#888" />
                  {showTooltip && (
                    <span className="tooltip-text">
                      {t("emailTemplates.tooltipInfo")}
                    </span>
                  )}
                </div>
              </div>

              <input
                type="text"
                className="email-configuration__input"
                placeholder={t("emailTemplates.enterSenderName")}
                value={emailConfig.senderName}
                onChange={(e) =>
                  setEmailConfig((prev) => ({
                    ...prev,
                    senderName: sanitizeInput(e.target.value, "text"),
                  }))
                }
              />
              <p className="error-msg">{bodyData.emailError}</p>
            </div>

            <div className="email-configuration__field" id="sender-email">
              <label className="email-configuration__label">
                {t("emailTemplates.senderEmail")}
              </label>
              <input
                type="email"
                className="email-configuration__input"
                placeholder={t("emailTemplates.enterSenderEmail")}
                value={emailConfig.senderEmail}
                onChange={(e) =>
                  setEmailConfig((prev) => ({
                    ...prev,
                    senderEmail: sanitizeInput(e.target.value, "email"),
                  }))
                }
              />
              <p className="error-msg">{bodyData.emailError}</p>
            </div>
          </div>

          <div className="template-customisations">
            <h4>{t("emailTemplates.templateCustomisations")}</h4>
            <div className="template-customisations__field">
              <label>{t("emailTemplates.merchantMessage")}</label>
              <textarea
                rows={5}
                value={bodyData.merchantMessage}
                onChange={(e) =>
                  setBodyData((prev) => ({
                    ...prev,
                    merchantMessage: sanitizeInput(e.target.value, "textarea"),
                  }))
                }
              />
            </div>

            <div className="template-customisations__field terms-and-conditions-editor">
              <label>Add Terms and Conditions</label>
              <MarkdownEditor
                termsAndConditions={termsAndConditions}
                setTermsAndConditions={setTermsAndConditions}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
